import React from 'react';
import {
    selectCapsuleSymbolBindingFromState,
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';

import {
    SignalTileDataState,
    SignalTileTimeElement,
} from 'widgets/signalTiles/signalTile/state/stream/SignalTileStreamState';
import { SignalTileConfigState } from 'widgets/signalTiles/signalTile/state/config/SignalTileConfigState';
import {
    buildSignalTileChartData,
    SignalChartTimeSeriesItem,
} from 'widgets/signalTiles/signalTile/components/chart/signalTileData';
import { signalTileStreamRequestBuilders } from 'widgets/signalTiles/signalTile/state/stream/SignalTileStreamRequest';
import { WidgetType } from 'widgets/WidgetType';
import { StreamedDataKey } from 'streamsState/store/streamedData/StreamedDataState';
import { initialRsiTileConfigState } from 'widgets/signalTiles/rsiTile/state';
import { initialPfeTileConfigState } from 'widgets/signalTiles/pfeTile/state';

export interface WidgetPanelSignalTileProps extends WidgetPanelDetailProps {
    mode: 'timeSeries' | 'data' | 'chartTimeSeries';
    upperBoundary: number;
    lowerBoundary: number;
    initialConfig: SignalTileConfigState;
}

export default function WidgetPanelSignalTile(
    props: WidgetPanelSignalTileProps
) {
    const {
        widgetState,
        mode,
        widget: { id: widgetKey, type: widgetType },
        upperBoundary,
        lowerBoundary,
        state,
        initialConfig,
    } = props;

    const config =
        widgetState?.config ?? (initialConfig as SignalTileConfigState);

    const symbolBinding = selectCapsuleSymbolBindingFromState(props);

    if (!symbolBinding) {
        return <></>;
    }

    const { symbol, source } = symbolBinding;

    const { toSignalRequestUid } = signalTileStreamRequestBuilders(
        widgetType,
        WidgetType.RSI_TILE
            ? initialRsiTileConfigState
            : initialPfeTileConfigState
    );
    const requestUid = toSignalRequestUid(symbol, config);
    const dataKey: StreamedDataKey =
        widgetType === WidgetType.RSI_TILE ? 'rsiTiles' : 'pfeTiles';
    const data = state.state.streams.data[dataKey][requestUid]?.data as
        | SignalTileDataState
        | undefined;

    const signalTileData = buildSignalTileChartData(
        upperBoundary,
        lowerBoundary,
        config,
        data
    );

    if (data) {
        for (const b of data.timeSeries) {
            const withTime = b as unknown as { time: number };
            withTime.time = b._i * 60000;
            showNiceDate(withTime, 'time');
        }
    }

    if (signalTileData) {
        for (const t of signalTileData.timeSeries) {
            showNiceDate(t, 'time');
        }
        showNiceDate(signalTileData, 'startTime');
        showNiceDate(signalTileData, 'endTime');
    }

    if (mode === 'timeSeries') {
        return data ? (
            <WidgetPanelGrid<SignalTileTimeElement>
                key="timeSeriesGrid"
                data={data.timeSeries}
                getRowDataId={(d) => d._i.toString()}
                csvFileName={`${widgetKey}-timeSeries`}
            />
        ) : (
            <></>
        );
    }

    if (mode === 'chartTimeSeries') {
        return signalTileData ? (
            <WidgetPanelGrid<SignalChartTimeSeriesItem>
                key="chartTimeSeriesGrid"
                data={signalTileData.timeSeries}
                getRowDataId={(d) => d.time.toString()}
                csvFileName={`${widgetKey}-data`}
                colDefs={getChartColDefs()}
            />
        ) : (
            <></>
        );
    }

    return (
        <div className="widget-panel">
            <div className="title">Rsi Capsule Data</div>
            <div className="field">
                <span className={'label'}>Symbol:</span>
                {symbol}
            </div>
            <div className="field">
                <span className={'label'}>Source</span>
                {source}
            </div>
            <div className="field">
                <span className="label">Request Uid:</span>
                {requestUid}
            </div>

            <JsonPanel data={signalTileData} />
        </div>
    );
}

export function addSignalTilePanes(
    lowerBoundary: number,
    upperBoundary: number,
    detailProps: WidgetPanelDetailProps,
    initialConfig: SignalTileConfigState,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const renderData = () => (
        <WidgetPanelSignalTile
            {...detailProps}
            mode="data"
            lowerBoundary={lowerBoundary}
            upperBoundary={upperBoundary}
            initialConfig={initialConfig}
        />
    );
    panes.push({
        menuItem: 'Data',
        render: renderData,
    });

    const renderTs = () => (
        <WidgetPanelSignalTile
            {...detailProps}
            mode="timeSeries"
            lowerBoundary={lowerBoundary}
            upperBoundary={upperBoundary}
            initialConfig={initialConfig}
        />
    );
    panes.push({
        menuItem: 'Time Series',
        render: renderTs,
    });
    const render = () => (
        <WidgetPanelSignalTile
            {...detailProps}
            mode="chartTimeSeries"
            lowerBoundary={lowerBoundary}
            upperBoundary={upperBoundary}
            initialConfig={initialConfig}
        />
    );
    panes.push({
        menuItem: 'Chart Time Series',
        render,
    });
}

function getChartColDefs(): ColDef[] {
    return ['time', 'timeDisplay', 'value', 'above', 'middle', 'below'].map(
        (key) => ({
            field: key,
            key,
            width: 120,
            resizable: true,
        })
    );
}
